import type { QueryClient } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { CoachListGrid } from "~/components/coach-list-grid";
import { FaqList } from "~/components/faq-list";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { ExploreMoreLinks } from "~/components/feature/explore-more-links";
import { HubPageHeader } from "~/components/feature/hub-page-header";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { MarkdownContent } from "~/components/markdown-content";
import { Subheading } from "~/components/ui/subheading";
import { getCategoryPageContent } from "~/lib/functions/coach-group-seo";
import {
  getCategoryBySlug,
  getSubcategoriesByCategorySlug,
} from "~/lib/functions/expertise";
import { coachQueryOptions } from "~/lib/query-options";
import { coachListJsonLdScripts } from "~/lib/shared/coach-list-head";
import { MIN_COACHES_FOR_SITEMAP } from "~/lib/shared/coach-listing-thresholds";
import { createBrandMeta } from "~/lib/shared/meta";

const getCategoryCoachesData = async (
  categorySlug: string,
  queryClient: QueryClient,
) => {
  const [coaches, category, page] = await Promise.all([
    queryClient.ensureQueryData(coachQueryOptions.byCategory(categorySlug)),
    getCategoryBySlug({ data: { slug: categorySlug } }),
    getCategoryPageContent({ data: { categorySlug } }),
  ]);
  const subcategories = await getSubcategoriesByCategorySlug({
    data: { categorySlug },
  });
  return { coaches, category, subcategories, categorySlug, page };
};

export const Route = createFileRoute(
  "/_public/coaches/category/$categorySlug/",
)({
  loader: async ({ context, params }) => {
    const data = await getCategoryCoachesData(
      params.categorySlug,
      context.queryClient,
    );
    await context.queryClient.ensureQueryData({
      queryKey: ["categoryCoaches", params.categorySlug],
      queryFn: () => data,
    });
    return data;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return createBrandMeta({
        title: "Coaches | Dream Coach Match",
        description:
          "Find professional coaches on Dream Coach Match. Connect with the right coach to help you achieve your personal and professional goals.",
        ogType: "website",
        canonical: `https://dreamcoachmatch.com/coaches/category/${params.categorySlug}`,
      });
    }

    const { category, page, coaches } = loaderData;
    const name = category?.listingName || category?.name || params.categorySlug;
    const categorySlug = params.categorySlug;

    const title = page?.seoTitle ?? `${name} Coaches | Dream Coach Match`;
    const description =
      page?.metaDescription ??
      `Find ${name} coaches on Dream Coach Match. Whether you're seeking personal growth, skill development, or specific ${name} expertise, connect with the right coach to guide you toward success.`;

    const metaConfig = createBrandMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: `Discover ${name} coaches on Dream Coach Match. Whether you're seeking personal growth, skill development, or specific expertise in ${name}, find the right coach to guide you toward success.`,
      ogImage: `https://dreamcoachmatch.com/img/og/category-${categorySlug}.jpg`,
      ogType: "website",
      ogUrl: `https://dreamcoachmatch.com/coaches/category/${categorySlug}`,
      twitterCard: "summary_large_image",
      twitterTitle: `Find ${name} Coaches | Dream Coach Match`,
      twitterDescription: `Connect with ${name} coaches who can help you achieve your goals. Coaching services specializing in ${name} development.`,
      twitterImage: `https://dreamcoachmatch.com/img/og/category-${categorySlug}.jpg`,
      canonical: `https://dreamcoachmatch.com/coaches/category/${categorySlug}`,
      // Thin category pages stay out of the index (sitemap-aligned), but
      // crawlers may still follow through to the coach profiles.
      noindexFollow: coaches.length < MIN_COACHES_FOR_SITEMAP,
      keywords: [
        name,
        "coach",
        "coaching",
        "professional development",
        "personal growth",
        categorySlug,
        `${name} coaching`,
        `find ${name} coach`,
        `${name} specialist`,
      ],
      author: "Dream Coach Match",
    });

    const pageUrl = `https://dreamcoachmatch.com/coaches/category/${categorySlug}`;

    const collectionPageStructuredData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${name} Coaches`,
      description: description,
      url: pageUrl,
      mainEntity: {
        "@type": "ItemList",
        name: `${name} Coaches Directory`,
        description: `Professional coaches specializing in ${name}`,
        itemListOrder: "https://schema.org/ItemListUnordered",
      },
    };

    return {
      ...metaConfig,
      scripts: coachListJsonLdScripts({
        collectionPage: collectionPageStructuredData,
        breadcrumbs: [
          { name: "Home", url: "https://dreamcoachmatch.com" },
          { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
          {
            name: "Categories",
            url: "https://dreamcoachmatch.com/coaches/category",
          },
          { name, url: pageUrl },
        ],
        faqItems: page?.faqItems,
      }),
    };
  },
  component: CategoryCoachesPage,
});

function CategoryCoachesPage() {
  const { categorySlug } = Route.useParams();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["categoryCoaches", categorySlug],
    queryFn: () => getCategoryCoachesData(categorySlug, queryClient),
  });

  const { coaches, category, subcategories, page } = data || {};
  const name = category?.listingName || category?.name || "";

  return (
    <>
      <HubPageHeader
        accentColorClass="bg-violet-200"
        heading={name ? `Looking for a ${name} Coach?` : "Looking for a Coach?"}
        subheading={
          name
            ? `Discover ${name} coaches on Dream Coach Match. Whether you're seeking personal growth, skill development, or specific ${name} expertise, find the right coach to guide you.`
            : "Discover coaches on Dream Coach Match."
        }
        ctaLabel="Find Your Coach"
        ctaHref={`/discovery?utm_campaign=category_coaches&utm_source=${categorySlug}`}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Coaches", to: "/coaches/category" },
          { label: "Categories", to: "/coaches/category" },
          { label: name || "Category" },
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {subcategories && subcategories.length > 0 && (
          <div className="my-12">
            <Subheading className="text-center">
              Explore {name} Subcategories
            </Subheading>
            <div className="flex flex-wrap justify-center gap-4">
              {subcategories.map((sub) => (
                <Link
                  key={sub.id}
                  to="/coaches/subcategory/$subcategorySlug"
                  params={{ subcategorySlug: sub.slug }}
                  className="rounded-lg border border-brand-300 bg-brand-100 px-4 py-2 text-sm font-medium text-brand-700 shadow-sm shadow-brand-800/15 transition-colors duration-150 ease-in-out hover:bg-brand-200 hover:shadow-md"
                >
                  {sub.name}
                </Link>
              ))}
            </div>
          </div>
        )}
        <CoachListGrid
          className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          coaches={coaches ?? []}
          emptyMessage="No coaches found in this category yet."
          midListCta={
            <MidListCTA
              breakdownName={name}
              breakdownTypeSlug={categorySlug}
              preLinkText="Not sure which coach to choose?"
              ctaLinkTextTemplate="Let us match you with the perfect coach!"
              utmCampaignPrefix="category_coaches_mid"
            />
          }
        />
        <BottomCTA
          itemCount={coaches?.length || 0}
          itemTypePlural="coaches"
          breakdownName={name}
          breakdownTypeSlug={categorySlug}
          joinAsLinkUrl="/page/coach"
          joinAsLinkText="Join as a coach."
          mainCtaSubTextTemplate="Ready to find your dream {} coach?"
          mainCtaButtonTextTemplate="Try the Matchmaker"
          utmCampaignPrefix="category_coaches_bottom"
        />
        <ExploreMoreLinks
          links={[
            { to: "/organizations", label: "Browse by Organization" },
            { to: "/training-programs", label: "Browse Training Programs" },
            { to: "/coaches/category", label: "All Categories" },
          ]}
        />
        {page?.contentMd && (
          <div className="mx-auto prose mt-12">
            <MarkdownContent content={page.contentMd} demoteH1 />
          </div>
        )}
        {page?.faqItems && page.faqItems.length > 0 && (
          <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
        )}
      </div>
    </>
  );
}
